/**
 * React hooks for navigation state management
 * 
 * Provides easy-to-use hooks for components to manage navigation state
 * including scroll position tracking and restoration
 */

import { useEffect, useCallback, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { NavigationState } from '../types/epic-types'
import { navigationStateManager, epicListNavigation, epicDetailsNavigation } from './navigationState'

interface UseNavigationStateOptions {
  /**
   * Storage key for the navigation state
   */
  storageKey: string

  /**
   * Function to get current navigation state
   */
  getCurrentState: () => NavigationState

  /**
   * Whether to automatically track scroll position
   * @default true
   */
  trackScroll?: boolean

  /**
   * Whether to handle browser refresh
   * @default true
   */
  handleRefresh?: boolean

  /**
   * Debounce delay for scroll tracking in milliseconds
   * @default 300
   */
  scrollDebounce?: number
}

interface UseNavigationStateReturn {
  /**
   * Save current navigation state
   */
  saveState: (state?: NavigationState) => void

  /**
   * Restore navigation state
   */
  restoreState: () => NavigationState | null

  /**
   * Clear saved navigation state
   */
  clearState: () => void

  /**
   * Save state before navigation
   */
  saveBeforeNavigation: (state?: NavigationState) => void

  /**
   * Restore scroll position
   */
  restoreScrollPosition: (position: number, smooth?: boolean) => void

  /**
   * Get current scroll position
   */
  getCurrentScrollPosition: () => number
}

/**
 * Hook for managing navigation state with automatic scroll tracking
 */
export function useNavigationState({
  storageKey,
  getCurrentState,
  trackScroll = true,
  handleRefresh = true,
  scrollDebounce = 300
}: UseNavigationStateOptions): UseNavigationStateReturn {
  const location = useLocation()
  const cleanupRef = useRef<(() => void) | null>(null)

  // Setup scroll tracking and refresh handling
  useEffect(() => {
    if (trackScroll) {
      navigationStateManager.startScrollTracking(storageKey, getCurrentState, scrollDebounce)
    }

    if (handleRefresh) {
      cleanupRef.current = navigationStateManager.handleBeforeUnload(storageKey, getCurrentState)
    }

    // Cleanup on unmount
    return () => {
      if (trackScroll) {
        navigationStateManager.stopScrollTracking(storageKey)
      }
      if (cleanupRef.current) {
        cleanupRef.current()
      }
    }
  }, [storageKey, getCurrentState, trackScroll, handleRefresh, scrollDebounce])

  // Save state when location changes (navigation)
  useEffect(() => {
    return () => {
      // Save state before component unmounts (navigation away)
      const currentState = getCurrentState()
      navigationStateManager.saveBeforeNavigation(storageKey, currentState)
    }
  }, [location.pathname, storageKey, getCurrentState])

  const saveState = useCallback((state?: NavigationState) => {
    const stateToSave = state || getCurrentState()
    navigationStateManager.saveState(storageKey, stateToSave)
  }, [storageKey, getCurrentState])

  const restoreState = useCallback(() => {
    return navigationStateManager.restoreState(storageKey)
  }, [storageKey])

  const clearState = useCallback(() => {
    navigationStateManager.clearState(storageKey)
  }, [storageKey])

  const saveBeforeNavigation = useCallback((state?: NavigationState) => {
    const stateToSave = state || getCurrentState()
    navigationStateManager.saveBeforeNavigation(storageKey, stateToSave)
  }, [storageKey, getCurrentState])

  const restoreScrollPosition = useCallback((position: number, smooth?: boolean) => {
    navigationStateManager.restoreScrollPosition(position, smooth)
  }, [])

  const getCurrentScrollPosition = useCallback(() => {
    return navigationStateManager.getCurrentScrollPosition()
  }, [])

  return {
    saveState,
    restoreState,
    clearState,
    saveBeforeNavigation,
    restoreScrollPosition,
    getCurrentScrollPosition
  }
}

/**
 * Specialized hook for epic list navigation state
 */
export function useEpicListNavigation(getCurrentState: () => NavigationState) {
  const location = useLocation()

  useEffect(() => {
    // Start scroll tracking
    epicListNavigation.startScrollTracking(getCurrentState)

    // Cleanup on unmount
    return () => {
      epicListNavigation.stopScrollTracking()
    }
  }, [getCurrentState])

  // Save state when navigating away
  useEffect(() => {
    return () => {
      const currentState = getCurrentState()
      epicListNavigation.saveBeforeNavigation(currentState)
    }
  }, [location.pathname, getCurrentState])

  return {
    saveState: useCallback((state?: NavigationState) => {
      const stateToSave = state || getCurrentState()
      epicListNavigation.save(stateToSave)
    }, [getCurrentState]),

    restoreState: useCallback(() => {
      return epicListNavigation.restore()
    }, []),

    clearState: useCallback(() => {
      epicListNavigation.clear()
    }, []),

    saveBeforeNavigation: useCallback((state?: NavigationState) => {
      const stateToSave = state || getCurrentState()
      epicListNavigation.saveBeforeNavigation(stateToSave)
    }, [getCurrentState]),

    restoreScrollPosition: useCallback((position: number, smooth?: boolean) => {
      navigationStateManager.restoreScrollPosition(position, smooth)
    }, []),

    getCurrentScrollPosition: useCallback(() => {
      return navigationStateManager.getCurrentScrollPosition()
    }, [])
  }
}

/**
 * Specialized hook for epic details navigation state
 */
export function useEpicDetailsNavigation(getCurrentState: () => NavigationState) {
  return {
    saveState: useCallback((state?: NavigationState) => {
      const stateToSave = state || getCurrentState()
      epicDetailsNavigation.save(stateToSave)
    }, [getCurrentState]),

    restoreState: useCallback(() => {
      return epicDetailsNavigation.restore()
    }, []),

    clearState: useCallback(() => {
      epicDetailsNavigation.clear()
    }, [])
  }
}

/**
 * Hook for restoring navigation state on component mount
 * Useful for restoring state when returning to a page
 */
export function useRestoreNavigationState(
  restoreState: () => NavigationState | null,
  onStateRestored: (state: NavigationState) => void,
  dependencies: any[] = []
) {
  useEffect(() => {
    const savedState = restoreState()
    if (savedState) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        onStateRestored(savedState)
      }, 0)
    }
  }, dependencies) // eslint-disable-line react-hooks/exhaustive-deps
}